import {
  DEFAULT_DRIVER_APP_NAME,
  DEFAULT_DRIVER_OFFICE_NAME,
} from '../constants/driver-app.constants';
import type { Organization } from '../models/organization';

export interface DriverAppBranding {
  appName: string;
  logo: string | null;
  brandColor: string | null;
}

type BrandingSource = Pick<
  Organization,
  'logo' | 'brandColor' | 'driverAppName' | 'driverAppLogo' | 'driverAppBrandColor'
>;

/**
 * What the driver app shows for an organization: its driver-app name, logo and
 * colour, else its main logo and colour, else the app defaults. Resolved once on
 * the server into `driver_access` and the public domains map, so the app never
 * sees an organization document.
 */
export function driverAppBranding(
  org: Partial<BrandingSource> | null | undefined,
): DriverAppBranding {
  return {
    appName: text(org?.driverAppName) ?? DEFAULT_DRIVER_APP_NAME,
    logo: text(org?.driverAppLogo) ?? text(org?.logo),
    brandColor: hexColor(org?.driverAppBrandColor) ?? hexColor(org?.brandColor),
  };
}

export interface DriverAppOfficeNaming {
  /** Shown to drivers in place of an office user's name. */
  officeName: string;
  /** Show the office user's own name instead. */
  showOfficeUserNames: boolean;
}

/** How the organization's office users appear to its drivers in chat. */
export function driverAppOfficeNaming(
  org:
    | Partial<Pick<Organization, 'driverAppOfficeName' | 'driverAppShowOfficeUserNames'>>
    | null
    | undefined,
): DriverAppOfficeNaming {
  return {
    officeName: text(org?.driverAppOfficeName) ?? DEFAULT_DRIVER_OFFICE_NAME,
    showOfficeUserNames: org?.driverAppShowOfficeUserNames === true,
  };
}

/**
 * The name a driver sees for an office user: the office's name, or with
 * `showOfficeUserNames` the user's own (the office's name when they have none).
 * Entries written before the setting existed hide names too.
 */
export function officeNameForDriver(
  naming: Partial<DriverAppOfficeNaming> | null | undefined,
  userName: string | null | undefined,
): string {
  const office = text(naming?.officeName) ?? DEFAULT_DRIVER_OFFICE_NAME;
  return naming?.showOfficeUserNames ? (text(userName) ?? office) : office;
}

/**
 * `#rgb`, `#rrggbb` or `#rrggbbaa`, lower-cased; anything else is null. The colour
 * goes into CSS and the web app manifest, so free text never reaches either.
 */
export function hexColor(value: string | null | undefined): string | null {
  const v = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(v) ? v : null;
}

/** Black or white, whichever reads better on `hex` (WCAG relative luminance). */
export function textColorOn(hex: string): '#ffffff' | '#111827' {
  const v = hexColor(hex);
  if (!v) {
    return '#ffffff';
  }
  const full = v.length === 4 ? v.replace(/[0-9a-f]/g, (c) => c + c) : v;
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(full.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  // Contrast with white is (1.05)/(L+0.05); with near-black roughly (L+0.05)/0.05.
  return 1.05 / (luminance + 0.05) >= (luminance + 0.05) / 0.0625 ? '#ffffff' : '#111827';
}

function text(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}
